import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, Send, Check, AlertTriangle, RotateCcw, Clock } from "lucide-react";
import { api, ApiError, type BlackoutWindowView, type CalendarSlotView, type SocialPlatform } from "../../lib/api.js";
import { useDismiss } from "../../components/ui/useDismiss.js";

/**
 * Approve several posts at once and hand them to Postiz, from the screen where
 * you can see them.
 *
 * WHY APPROVING ALSO SENDS. This dialog used to only flip the status to
 * `approved` and leave delivery to the drip. The drip is off by default
 * (`SOCIAL_DRIP=off`), so on 2026-09-14 thirteen posts were approved here and
 * nothing at all reached Postiz — from the operator's side the button did
 * nothing. Approving here now means: approved, and sent to Postiz as a
 * SCHEDULED post that publishes at its slot with no second step in Postiz.
 * The same path as the single-post "שליחה לפוסטיז" button (`sendVariantById`
 * in schedule mode), one post at a time.
 *
 * A post that was approved earlier but never reached Postiz is listed too —
 * those are exactly the thirteen that were stranded.
 *
 * DELIBERATELY NOT A SINGLE "APPROVE EVERYTHING" PRESS. This lists what would
 * go out, with its date and channel, pre-ticked, and lets the operator untick;
 * the send itself takes a second press, because it ends with posts live under
 * the brand.
 */

const PLATFORM_HE: Record<SocialPlatform, string> = {
  facebook: "פייסבוק", instagram: "אינסטגרם", tiktok: "טיקטוק", youtube: "יוטיוב",
};

const FORMAT_HE: Record<string, string> = {
  feed_link: "פוסט+לינק", feed_image: "תמונה", carousel: "קרוסלה", reel: "ריל",
  story: "סטורי", video: "וידאו", photo_post: "תמונות", short: "שורט", long_video: "סרטון ארוך",
};

const TZ = "Asia/Jerusalem";
const humanIl = (iso: string) =>
  new Intl.DateTimeFormat("he-IL", {
    timeZone: TZ, weekday: "short", day: "numeric", month: "short",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).format(new Date(iso));

/** Postiz publishes a post whose time has passed immediately; the server refuses under 5 minutes. */
const SEND_LEAD_MS = 5 * 60 * 1000;

/**
 * What can go out now, and why the rest cannot. Exported so the calendar's
 * button counts exactly what this dialog will act on.
 */
export function splitForSending(slots: CalendarSlotView[], now = Date.now(), blackouts: BlackoutWindowView[] = []) {
  const ready: CalendarSlotView[] = [];
  const blocked: { slot: CalendarSlotView; why: string }[] = [];
  for (const s of slots) {
    if (s.status === "scheduled" || s.status === "posted" || s.status === "skipped") continue;
    if (s.postizPostId) continue;
    if (s.status === "awaiting_media") { blocked.push({ slot: s, why: "ממתין לווידאו" }); continue; }
    if (!s.checksOk) { blocked.push({ slot: s, why: "נכשל בבדיקות" }); continue; }
    if (!s.hasMedia) { blocked.push({ slot: s, why: "אין מדיה" }); continue; }
    if (Date.parse(s.scheduledFor) < now + SEND_LEAD_MS) { blocked.push({ slot: s, why: "זמן הפרסום עבר, להזיז קודם" }); continue; }
    // Postiz would publish it right inside Shabbat / the chag. Move it first.
    const w = blackouts.find((b) => Date.parse(b.startIso) <= Date.parse(s.scheduledFor) && Date.parse(s.scheduledFor) < Date.parse(b.endIso));
    if (w) { blocked.push({ slot: s, why: `בזמן חסום (${w.labelHe}), להזיז קודם` }); continue; }
    ready.push(s);
  }
  return { ready, blocked };
}

/**
 * `limited`: Postiz's hourly create limit refused it. Postiz then blocks for the
 * rest of the hour, so the run stops there instead of rendering and uploading
 * every remaining post only to be refused again. The rest are `pending`.
 */
type Outcome = { id: number; ok: boolean; error?: string; limited?: boolean; pending?: boolean };

const waitHe = (sec: number) => {
  const min = Math.ceil(sec / 60);
  return min <= 1 ? "בעוד דקה" : `בעוד ${min} דקות`;
};

export function ApproveDialog({
  slots, blackouts = [], onClose, onDone,
}: {
  /** Everything currently in view. Filtered here, not by the caller. */
  slots: CalendarSlotView[];
  blackouts?: BlackoutWindowView[];
  onClose: () => void;
  onDone: () => void;
}) {
  const dismiss = useDismiss(onClose);
  const { ready, blocked } = useMemo(() => splitForSending(slots, Date.now(), blackouts), [slots, blackouts]);

  const [picked, setPicked] = useState<Set<number>>(() => new Set(ready.map((s) => s.id)));
  const [armed, setArmed] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [outcomes, setOutcomes] = useState<Outcome[] | null>(null);
  const [retryAfterSec, setRetryAfterSec] = useState<number | null>(null);

  const quotaQ = useQuery({ queryKey: ["postiz-quota"], queryFn: () => api.postizQuota(), staleTime: 0 });
  const quota = quotaQ.data?.quota ?? null;
  const blockedSec = quota?.blockedUntil ? Math.max(0, (Date.parse(quota.blockedUntil) - Date.now()) / 1000) : 0;

  const run = useMutation({
    mutationFn: async (ids: number[]) => {
      // ONE AT A TIME, in slot order, every failure kept with its reason. Each
      // send renders the slides and uploads them to Postiz; firing them in
      // parallel would load the renderer and make a partial failure impossible
      // to describe.
      const order = ready.filter((s) => ids.includes(s.id));
      const prior = (outcomes ?? []).filter((o) => !ids.includes(o.id));
      const out: Outcome[] = [];
      setRetryAfterSec(null);
      setProgress({ done: 0, total: order.length });
      for (let i = 0; i < order.length; i++) {
        const s = order[i]!;
        try {
          if (s.status !== "approved") await api.updateSocialVariant(s.id, { status: "approved" });
          const r = await api.sendSocialVariant(s.id, "schedule");
          out.push(r.ok ? { id: s.id, ok: true } : { id: s.id, ok: false, error: r.error ?? "השליחה נכשלה" });
        } catch (e) {
          const limited = e instanceof ApiError && e.status === 429;
          out.push({ id: s.id, ok: false, error: (e as Error).message, ...(limited ? { limited: true } : {}) });
          if (limited) {
            const sec = Number((e as ApiError).data?.retryAfterSec);
            setRetryAfterSec(Number.isFinite(sec) && sec > 0 ? sec : 3600);
            for (const rest of order.slice(i + 1)) out.push({ id: rest.id, ok: false, pending: true });
            setOutcomes([...prior, ...out]);
            break;
          }
        }
        setProgress({ done: out.length, total: order.length });
        setOutcomes([...prior, ...out]);
      }
      void quotaQ.refetch();
      return out;
    },
    onSettled: () => setArmed(false),
  });

  const toggle = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const byId = new Map(slots.map((s) => [s.id, s]));
  const finished = run.isSuccess;
  const okCount = outcomes?.filter((o) => o.ok).length ?? 0;
  const failed = outcomes?.filter((o) => !o.ok && !o.limited && !o.pending) ?? [];
  const unsent = outcomes?.filter((o) => o.limited || o.pending) ?? [];

  return (
    <div {...dismiss} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" dir="rtl">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold">אישור ושליחה לפוסטיז</h2>
          <button onClick={onClose} disabled={run.isPending} className="text-sm text-muted hover:text-ink disabled:opacity-40">סגירה</button>
        </header>

        <div className="grow space-y-3 overflow-auto p-4 text-sm">
          <p className="rounded-lg bg-canvas px-3 py-2 text-[11px] text-muted">
            כל פוסט מסומן <strong className="text-ink">מאושר</strong> ונשלח לפוסטיז כפוסט
            <strong className="text-ink"> מתוזמן</strong>. הוא יתפרסם אוטומטית בזמן שלו, בלי אישור נוסף בפוסטיז.
            פוסטים שאושרו קודם ולא הגיעו לפוסטיז מופיעים כאן גם.
          </p>

          {!finished && blockedSec > 0 && (
            <p className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
              <Clock size={12} /> פוסטיז הגיע למגבלת הפוסטים לשעה ויקבל פוסטים שוב {waitHe(blockedSec)}.
            </p>
          )}
          {!finished && blockedSec === 0 && quota?.remaining != null && (
            <p className={`text-[11px] ${picked.size > quota.remaining ? "text-amber-800" : "text-muted"}`}>
              פוסטיז מקבל עוד {quota.remaining} פוסטים בשעה הנוכחית{quota.limit != null ? ` (מתוך ${quota.limit})` : ""}.
              {picked.size > quota.remaining && " השליחה תיעצר כשהמכסה תיגמר, והשאר יחכו לשליחה חוזרת."}
            </p>
          )}

          {ready.length === 0 && blocked.length === 0 && (
            <p className="text-xs text-muted">אין פוסטים שממתינים לשליחה בטווח הזה.</p>
          )}

          {ready.map((s) => {
            const o = outcomes?.find((x) => x.id === s.id);
            return (
              <label key={s.id} className="flex items-start gap-2 rounded-lg border border-line px-3 py-2">
                <input
                  type="checkbox"
                  checked={picked.has(s.id)}
                  onChange={() => toggle(s.id)}
                  disabled={run.isPending || finished}
                  className="mt-0.5"
                />
                <span className="min-w-0 grow">
                  <span className="block truncate text-xs font-medium">{s.label ?? s.campaign}</span>
                  <span className="block text-[11px] text-muted">
                    {PLATFORM_HE[s.platform]} · {FORMAT_HE[s.format] ?? s.format} · {humanIl(s.scheduledFor)}
                    {s.status === "approved" && " · אושר, טרם נשלח"}
                  </span>
                  <span className="mt-0.5 block truncate text-[11px] opacity-70">{s.hook}</span>
                </span>
                {o && (o.ok
                  ? <span className="shrink-0 text-[11px] font-medium text-emerald-700">נשלח ✓</span>
                  : o.limited || o.pending
                    ? <span className="shrink-0 text-[11px] font-medium text-amber-700">ממתין</span>
                    : <span className="shrink-0 text-[11px] font-medium text-danger">נכשל</span>)}
              </label>
            );
          })}

          {blocked.length > 0 && (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
              <p className="mb-1 flex items-center gap-1 font-medium">
                <AlertTriangle size={12} /> לא ניתנים לשליחה ({blocked.length}):
              </p>
              <ul className="space-y-0.5">
                {blocked.map(({ slot: s, why }) => (
                  <li key={s.id} className="truncate">
                    {PLATFORM_HE[s.platform]} · {FORMAT_HE[s.format] ?? s.format} — {s.label ?? s.campaign} · <strong>{why}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {finished && (
            <div className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
              <p className="flex items-center gap-1 font-medium"><Check size={13} /> נשלחו לפוסטיז {okCount} פוסטים.</p>
              {failed.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-danger">
                  {failed.map((f) => {
                    const s = byId.get(f.id);
                    return (
                      <li key={f.id}>
                        {s ? `${PLATFORM_HE[s.platform]} · ${FORMAT_HE[s.format] ?? s.format} (${humanIl(s.scheduledFor)})` : `#${f.id}`} — {f.error}
                      </li>
                    );
                  })}
                </ul>
              )}
              {unsent.length > 0 && (
                <p className="mt-1 text-amber-900">
                  {unsent.length} לא נשלחו: פוסטיז הגיע למגבלת הפוסטים לשעה
                  {retryAfterSec ? ` ויקבל פוסטים שוב ${waitHe(retryAfterSec)}` : ""}. הם מאושרים ונשארים בתור, שום דבר לא אבד.
                </p>
              )}
            </div>
          )}
        </div>

        <footer className="flex items-center gap-2 border-t border-line px-4 py-3">
          <span className="text-xs text-muted">
            {run.isPending && progress ? `שולח ${progress.done + 1} מתוך ${progress.total}…` : `${picked.size} מסומנים`}
          </span>
          <span className="grow" />
          {finished ? (
            <>
              {unsent.length > 0 && (
                <button
                  onClick={() => run.mutate(unsent.map((o) => o.id))}
                  className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-2 text-sm"
                  title="פוסטיז חוסם עד סוף השעה. לחיצה מוקדמת פשוט תיעצר שוב, בלי נזק."
                >
                  <RotateCcw size={14} /> שליחה חוזרת ({unsent.length})
                </button>
              )}
              <button onClick={onDone} className="rounded-lg bg-brand-700 px-3 py-2 text-sm font-medium text-white">
                סגירה ורענון
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} disabled={run.isPending} className="rounded-lg border border-line px-3 py-2 text-sm disabled:opacity-40">ביטול</button>
              <button
                onClick={() => (armed ? run.mutate([...picked]) : setArmed(true))}
                disabled={picked.size === 0 || run.isPending}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-white disabled:opacity-40 ${armed ? "bg-emerald-800" : "bg-emerald-700"}`}
              >
                {run.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {armed ? `לאשר: ${picked.size} פוסטים יתפרסמו בזמנם` : `אישור ושליחה (${picked.size})`}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
